// The review outbox's CLOSED SETS (plan 102, incident developerz-ai/ultimate#969), one
// source for every reader: the `review_outbox` CHECKs (`@developerz/db`
// `schema/review-outbox-sets.ts` imports these tuples), the box -> mothership put schema
// (`@developerz/nats` `review-outbox.ts`) and the `review.outbox_*` audit payloads. They
// live HERE because `@developerz/nats` may not import `@developerz/db`; the
// `MOVE_BATCH_STATES` shape (`entities/runner/move-verdict.ts`).
//
// Stored and wire vocabulary: a member is never renamed once a row or a box carries it, and
// a member a box will SEND reaches the mothership's reader one release first
// (`scripts/closed-set-release.test.ts`).

/** The GitHub review `event` the box froze, in GitHub's wire spelling (upper case). */
export const REVIEW_OUTBOX_EVENTS = ['APPROVE', 'REQUEST_CHANGES', 'COMMENT'] as const;
export type ReviewOutboxEvent = (typeof REVIEW_OUTBOX_EVENTS)[number];

/** GitHub's `side` / `start_side` of an inline review comment. */
export const REVIEW_OUTBOX_COMMENT_SIDES = ['LEFT', 'RIGHT'] as const;
export type ReviewOutboxCommentSide = (typeof REVIEW_OUTBOX_COMMENT_SIDES)[number];

/**
 * Why an outbox ended unposted. Named, never a NULL standing for several reasons.
 *
 * - `pr_closed` — the PR closed or merged before the final part landed.
 * - `installation_gone` — GitHub answered 404 or refused the installation (401/403), so
 *   nothing could be read back or posted.
 * - `superseded` — reserved for the hold seam (plan 102 slice 04); nothing writes it yet.
 * - `pr_locked` — the PR's conversation is locked (`locked: true` on the PR read, or a
 *   final-review 422 saying `lock prevents review`): GitHub refuses every review the App
 *   posts until a maintainer unlocks it, an owner's choice and not a platform fault.
 */
export const REVIEW_OUTBOX_RETIRED_REASONS = [
  'pr_closed',
  'installation_gone',
  'superseded',
  'pr_locked',
] as const;
export type ReviewOutboxRetiredReason = (typeof REVIEW_OUTBOX_RETIRED_REASONS)[number];

/**
 * Why one comment left unposted: its own two (GitHub refused the anchor; the hunk changed
 * under a moved head), plus every reason that retires its whole outbox, so retiring an
 * outbox retires its pending rows under the same name.
 */
export const REVIEW_OUTBOX_COMMENT_RETIRED_REASONS = [
  'anchor_refused_422',
  'hunk_moved',
  ...REVIEW_OUTBOX_RETIRED_REASONS,
] as const;
export type ReviewOutboxCommentRetiredReason =
  (typeof REVIEW_OUTBOX_COMMENT_RETIRED_REASONS)[number];

/**
 * Why the drain changed the frozen event on the wire. Not a closed set any box sends: the
 * drain alone writes it, onto the `review_posted` row as `wire_downgrade`.
 * `head_moved` — an APPROVE posted as a COMMENT because the live head is not the reviewed one.
 */
export const REVIEW_OUTBOX_WIRE_DOWNGRADES = ['head_moved'] as const;
export type ReviewOutboxWireDowngrade = (typeof REVIEW_OUTBOX_WIRE_DOWNGRADES)[number];
